'use client';

import React from 'react';
import { useNode } from '@craftjs/core';
import NewVehiclesSection from '@/sections/vehicles/new-vehicles-section';
import type { CardTitleField, SpecSlot } from '@/lib/vehicleCardFields';
import type { ModoAlcanceCategorias } from '@/lib/categoryScope';

interface TraditionalVehicleGridProps {
  /**
   * Cuantas tarjetas se montan de entrada; el resto llega con "Ver más".
   *
   * A proposito NO esta en `craft.props`, por la misma razon que `cardSpec1..5`:
   * si estuviera, los nodos de las paginas YA PUBLICADAS lo recibirian
   * materializado. Ausente, el renderer cae a su default y no hay que migrar
   * ni un byte del arbol guardado.
   */
  initialVehicleCount?: number;
  title?: string;
  accentColor?: string;
  sectionBgColor?: string;
  filterStyle?: 'buttons' | 'images';
  /** Barra para buscar por texto dentro del listado. Apagada = como siempre. */
  showSearch?: boolean;
  /**
   * Cómo se presentan los filtros: 'panel' (el `<aside>` de siempre) o 'bar'
   * (grilla de desplegables arriba del listado). Ausente = 'panel', así que
   * los sitios ya publicados no cambian.
   */
  filtersLayout?: 'panel' | 'bar';
  /** Apaga la barra pegada de categorías: ver `new-vehicles-section`. */
  showCategoryTabs?: boolean;
  /**
   * Con qué estado arranca el panel de filtros en escritorio: 'open' o
   * 'closed'. A propósito NO está en `craft.props`, por la misma razón que
   * `initialVehicleCount`: si estuviera, los nodos ya publicados lo recibirían
   * materializado. Ausente, el renderer cae a 'closed', que es lo que se ve
   * hoy, y no hay que migrar ni un byte del árbol guardado.
   */
  filtersInitialState?: 'open' | 'closed';
  filterBarBgColor?: string;
  filterBarBorderColor?: string;
  filterTextColor?: string;
  filterActiveTextColor?: string;
  filterHoverBgColor?: string;
  categoryImage_all?: string;
  categoryImage_SUV?: string;
  categoryImage_Sedan?: string;
  categoryImage_Hatchback?: string;
  categoryImage_Pickup?: string;
  categoryImage_Van?: string;
  categoryImage_Coupe?: string;
  categoryImage_Wagon?: string;
  cardTitleField?: CardTitleField;
  /**
   * Datos de la tarjeta, en orden. A propósito NO están en `craft.props`: si
   * estuvieran, cualquier nodo —incluidos los de las páginas ya publicadas— los
   * recibiría materializados y la tarjeta los leería como "configurado",
   * cambiando cómo dibuja los datos que faltan. Ausentes, `resolveSpecSlots`
   * cae a `DEFAULT_SPEC_SLOTS`, que es exactamente lo que se ve hoy.
   */
  cardSpec1?: SpecSlot;
  cardSpec2?: SpecSlot;
  cardSpec3?: SpecSlot;
  cardSpec4?: SpecSlot;
  cardSpec5?: SpecSlot;
  cardBgColor?: string;
  cardBorderColor?: string;
  cardTitleColor?: string;
  cardSubtitleColor?: string;
  cardSpecsColor?: string;
  cardPriceColor?: string;
  // Badge visibility
  showBadgeCondition?: boolean;
  showBadgePromo?: boolean;
  showBadgeNew?: boolean;
  showBadgeCustom?: boolean;
  showRibbonSold?: boolean;
  showRibbonReserved?: boolean;
  showBadgeDiscount?: boolean;
  // Grid columns
  gridColsSm?: string;
  gridColsMd?: string;
  gridColsLg?: string;
  gridColsXl?: string;
  offerTabLabel?: string;
  offerTabFilter?: string;
  /**
   * Alcance de categorías de ESTE catálogo (ticket #86): `only` lo deja con las
   * categorías elegidas, `except` las saca.
   *
   * A propósito NO están en `craft.props`, por la misma razón que
   * `initialVehicleCount` y `cardSpec1..5`: si estuvieran, los nodos de las
   * páginas YA PUBLICADAS las recibirían materializadas. Ausentes, el catálogo
   * cae a 'all' y sigue mostrando el inventario completo, sin migrar un byte
   * del árbol guardado.
   */
  categoryScopeMode?: ModoAlcanceCategorias;
  categoryScopeIds?: (number | string)[];
  /**
   * Nombres de las categorías elegidas, escritos por el panel junto a los ids
   * (ticket #97). Sólo deciden qué pestañas se dibujan; el filtrado sigue
   * siendo por id.
   */
  categoryScopeNames?: string[];
  /**
   * Pone los disponibles antes que los reservados y los vendidos (ticket
   * #129). Dentro de cada grupo sigue mandando el orden elegido.
   *
   * A propósito NO está en `craft.props`, por la misma razón que
   * `initialVehicleCount` y `cardSpec1..5`: si estuviera, los nodos de las
   * páginas YA PUBLICADAS lo recibirían materializado. Ausente, el listado cae
   * a `false` —el orden de hoy, con reservados y vendidos intercalados— y no
   * hay que migrar ni un byte del árbol guardado.
   */
  prioritizeAvailable?: boolean;
  noResultsTitle?: string;
  noResultsText?: string;
  noResultsButtonText?: string;
}

function isDark(hex: string): boolean {
  const c = hex.replace('#', '');
  if (c.length !== 6) return false;
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b < 0.4;
}

export const TraditionalVehicleGrid = ({
  cardTitleField = 'model',
  cardSpec1,
  cardSpec2,
  cardSpec3,
  cardSpec4,
  cardSpec5,
  sectionBgColor,
  cardBgColor,
  cardBorderColor,
  cardTitleColor,
  cardSubtitleColor,
  cardSpecsColor,
  cardPriceColor,
  accentColor,
  filterStyle = 'buttons',
  showSearch = false,
  filtersInitialState,
  filtersLayout,
  showCategoryTabs,
  filterBarBgColor,
  filterBarBorderColor,
  filterTextColor,
  filterActiveTextColor,
  categoryImage_all,
  categoryImage_SUV,
  categoryImage_Sedan,
  categoryImage_Hatchback,
  categoryImage_Pickup,
  categoryImage_Van,
  categoryImage_Coupe,
  categoryImage_Wagon,
  showBadgeCondition = true,
  showBadgePromo = true,
  showBadgeNew = true,
  showBadgeCustom = true,
  showRibbonSold = true,
  showRibbonReserved = true,
  showBadgeDiscount = true,
  gridColsSm = '2',
  gridColsMd = '3',
  gridColsLg = '3',
  gridColsXl = '4',
  offerTabLabel,
  offerTabFilter,
  categoryScopeMode,
  categoryScopeIds,
  categoryScopeNames,
  initialVehicleCount,
  prioritizeAvailable
}: TraditionalVehicleGridProps) => {
  const {
    connectors: { connect },
    selected,
  } = useNode((state) => ({ selected: state.events.selected }));

  const darkMode = (sectionBgColor && isDark(sectionBgColor)) || (cardBgColor && isDark(cardBgColor));

  return (
    <div
      ref={(ref: HTMLDivElement | null) => { if (ref) connect(ref); }}
      className={`${selected ? 'ring-2 ring-dashed ring-slate-400' : ''} ${darkMode ? 'dark' : ''}`}
      style={sectionBgColor ? { backgroundColor: sectionBgColor } : undefined}
    >
      <NewVehiclesSection
        minimal
        initialVehicleCount={initialVehicleCount}
        cardTitleField={cardTitleField}
        cardSpec1={cardSpec1}
        cardSpec2={cardSpec2}
        cardSpec3={cardSpec3}
        cardSpec4={cardSpec4}
        cardSpec5={cardSpec5}
        showSearch={showSearch}
        filtersInitialState={filtersInitialState}
        filtersLayout={filtersLayout}
        showCategoryTabs={showCategoryTabs}
        filterStyle={filterStyle}
        filterBarBgColor={filterBarBgColor}
        filterBarBorderColor={filterBarBorderColor}
        filterTextColor={filterTextColor}
        filterActiveTextColor={filterActiveTextColor}
        accentColor={accentColor}
        sectionBgColor={sectionBgColor}
        categoryImage_all={categoryImage_all}
        categoryImage_SUV={categoryImage_SUV}
        categoryImage_Sedan={categoryImage_Sedan}
        categoryImage_Hatchback={categoryImage_Hatchback}
        categoryImage_Pickup={categoryImage_Pickup}
        categoryImage_Van={categoryImage_Van}
        categoryImage_Coupe={categoryImage_Coupe}
        categoryImage_Wagon={categoryImage_Wagon}
        showBadgeCondition={showBadgeCondition}
        showBadgePromo={showBadgePromo}
        showBadgeNew={showBadgeNew}
        showBadgeCustom={showBadgeCustom}
        showRibbonSold={showRibbonSold}
        showRibbonReserved={showRibbonReserved}
        showBadgeDiscount={showBadgeDiscount}
        gridColsSm={gridColsSm}
        gridColsMd={gridColsMd}
        gridColsLg={gridColsLg}
        gridColsXl={gridColsXl}
        offerTabLabel={offerTabLabel}
        offerTabFilter={offerTabFilter}
        categoryScopeMode={categoryScopeMode}
        categoryScopeIds={categoryScopeIds}
        categoryScopeNames={categoryScopeNames}
        prioritizeAvailable={prioritizeAvailable}
        cardBgColor={cardBgColor}
        cardBorderColor={cardBorderColor}
        cardTitleColor={cardTitleColor}
        cardSubtitleColor={cardSubtitleColor}
        cardSpecsColor={cardSpecsColor}
        cardPriceColor={cardPriceColor}
      />
    </div>
  );
};

(TraditionalVehicleGrid as any).craft = {
  displayName: 'TraditionalVehicleGrid',
  props: {
    title: '',
    accentColor: '',
    sectionBgColor: '',
    filterStyle: 'buttons',
    showSearch: false,
    filterBarBgColor: '#ffffff',
    filterBarBorderColor: '#e5e7eb',
    filterTextColor: '#374151',
    filterActiveTextColor: '#ffffff',
    filterHoverBgColor: '#f3f4f6',
    categoryImage_all: '',
    categoryImage_SUV: '',
    categoryImage_Sedan: '',
    categoryImage_Hatchback: '',
    categoryImage_Pickup: '',
    categoryImage_Van: '',
    categoryImage_Coupe: '',
    categoryImage_Wagon: '',
    cardTitleField: 'model',
    cardBgColor: '#ffffff',
    cardBorderColor: 'rgba(0,0,0,0.1)',
    cardTitleColor: '#171717',
    cardSubtitleColor: '#525252',
    cardSpecsColor: '#262626',
    cardPriceColor: '#171717',
    showBadgeCondition: true,
    showBadgePromo: true,
    showBadgeNew: true,
    showBadgeCustom: true,
    showRibbonSold: true,
    showRibbonReserved: true,
    showBadgeDiscount: true,
    gridColsSm: '2',
    gridColsMd: '3',
    gridColsLg: '3',
    gridColsXl: '4',
    offerTabLabel: '',
    offerTabFilter: '',
    noResultsTitle: 'Sin resultados',
    noResultsText: 'No encontramos vehículos con esos filtros',
    noResultsButtonText: 'Ver todos los vehículos',
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
  },
};
